import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, Database, UserCheck, FileText } from 'lucide-react';
export function PrivacyPolicyPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-961" data-mp="1xqd5bhgodrch">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-962" data-mp="1xsh63qja2a8j">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-963" data-mp="22ranvv5lknei">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-964" data-mp="1eybwpne3mmpv">
            <span className="w-8 h-px bg-teal" data-id="element-965" data-mp="1j1gw3h2vu8aa"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-966" data-mp="1iyvv0uaerwdl">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-967" data-mp="2ufui0ge2sdno"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-968" data-mp="2ui8durgkxtwa">
            Privacy Policy
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-969" data-mp="8qwi2okozn97">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-970" data-mp="3qwiyh75szpeo">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="prose prose-lg max-w-none" data-id="element-971" data-mp="3m3gdhnf3doa3">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-972" data-mp="335n9siwn9abn">
            At SyNeural, we are committed to protecting your privacy and
            handling your personal information with care and transparency. This
            Privacy Policy explains how we collect, use, store, and protect your
            information when you use our platform, website, and services.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-973" data-mp="2oix0au8robez">
            By using SyNeural's services, you agree to the practices described
            in this Privacy Policy. If you do not agree with our policies and
            practices, please do not use our services.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-974" data-mp="32hwlfrx2m86e">
        {/* Information We Collect */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-975" data-mp="37fps0m2ad477">
          <div className="flex items-center gap-4 mb-6" data-id="element-976" data-mp="1ogrx85w6cejg">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-977" data-mp="1jj8kjaf4k9tm">
              <Database size={24} data-id="element-978" data-mp="1xawub71wim7f" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-979" data-mp="1ta37yi43xfgn">
              Information We Collect
            </h2>
          </div>

          <div className="space-y-6" data-id="element-980" data-mp="15ksdtjh9ijbh">
            <div data-id="element-981" data-mp="10l5o4ak8zals">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-982" data-mp="10lp77ulmw51e">
                Personal Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-983" data-mp="1elqathta9qkk">
                We may collect personal information that you provide directly to
                us, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-984" data-mp="2pysn40ocntyx">
                <li data-id="element-985" data-mp="2kzz98755i0y8">Name, email address, and contact information</li>
                <li data-id="element-986" data-mp="3gbfjkkr1kazd">Organization or institutional affiliation</li>
                <li data-id="element-987" data-mp="3gd0dc85j7pw6">Account credentials and authentication information</li>
                <li data-id="element-988" data-mp="3lb1wet2yjp7n">Profile information and preferences</li>
                <li data-id="element-989" data-mp="3q9mwmsi7m8w3">Communication history and support inquiries</li>
              </ul>
            </div>

            <div data-id="element-990" data-mp="3v86iyu0ptmoz">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-991" data-mp="3v8q22e23qh4l">
                Assessment and Platform Data
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-992" data-mp="32kvkiyu228r">
                When you use our CJI and NCJI assessment tools, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-993" data-mp="cvi888h58wzg">
                <li data-id="element-994" data-mp="htthoi3j15h5">Assessment responses and performance data</li>
                <li data-id="element-995" data-mp="hvefcgl514j8">Reasoning patterns and decision-making metrics</li>
                <li data-id="element-996" data-mp="muhitwlgz2p3">Development pathway selections and progress tracking</li>
                <li data-id="element-997" data-mp="qv9pqj6vcvi7">
                  Aggregated and anonymized capability data for research
                  purposes
                </li>
              </ul>
            </div>

            <div data-id="element-998" data-mp="27a5u1og5wb81">
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-999" data-mp="279m9dzz4n8qb">
                Technical Information
              </h3>
              <p className="text-ink-muted leading-relaxed mb-3" data-id="element-1000" data-mp="22dowy4qzimit">
                We automatically collect certain technical information,
                including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1001" data-mp="2uzgn0jr9t1bm">
                <li data-id="element-1002" data-mp="2q2hsi03p2scw">Device information, browser type, and operating system</li>
                <li data-id="element-1003" data-mp="2l9oojeg1vo1v">IP address and general location data</li>
                <li data-id="element-1004" data-mp="2l83vxudaqf6n">Usage data, page views, and interaction patterns</li>
                <li data-id="element-1005" data-mp="2gb3nxdwvz2cw">Cookies and similar tracking technologies</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* How We Use Your Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1006" data-mp="38z9a853l8zeu">
          <div className="flex items-center gap-4 mb-6" data-id="element-1007" data-mp="xc62hocnzp1t">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1008" data-mp="12b8w8cn728uh">
              <Eye size={24} data-id="element-1009" data-mp="ne0075my4gwf" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1010" data-mp="nd6vea7js1gx">
              How We Use Your Information
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1011" data-mp="eed3gx55vz9p">
            We use the information we collect for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1012" data-mp="jf0p13amfnwj">
            <li data-id="element-1013" data-mp="28sdy15toi03z">To provide, maintain, and improve our platform and services</li>
            <li data-id="element-1014" data-mp="2dpw8j0ncgne3">
              To deliver personalized assessment results, insights, and
              development recommendations
            </li>
            <li data-id="element-1015" data-mp="2nsi1wb9wm4bp">
              To conduct research and analysis to advance our understanding of
              judgment, decision-quality, and human capability
            </li>
            <li data-id="element-1016" data-mp="2wor41xp9nh1j">
              To communicate with you about your account, updates, and support
            </li>
            <li data-id="element-1017" data-mp="2rud0h80ga3ls">To ensure platform security and prevent fraud or misuse</li>
            <li data-id="element-1018" data-mp="36k2kqlk8iybx">
              To comply with legal obligations and enforce our terms of service
            </li>
            <li data-id="element-1019" data-mp="31m10hxb2osyv">
              To develop new features, tools, and institutional applications
            </li>
          </ul>
        </motion.div>

        {/* Data Sharing and Disclosure */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1020" data-mp="3bkyisihwnq9f">
          <div className="flex items-center gap-4 mb-6" data-id="element-1021" data-mp="25r3z74mty3pu">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1022" data-mp="2apnjz1pot9gm">
              <UserCheck size={24} data-id="element-1023" data-mp="2fi8d010rrij3" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1024" data-mp="2jir5zyeeeumv">
              Data Sharing and Disclosure
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1025" data-mp="1mtta2cuwwqo4">
            We do not sell your personal information. We may share your
            information in the following circumstances:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1026" data-mp="1vsemrrdpdbgz">
            <li data-id="element-1027" data-mp="20sj5lo232iad">
              <strong data-id="element-1028" data-mp="20t2i2p8yseh0">With Your Consent:</strong> When you explicitly authorize
              us to share your information
            </li>
            <li data-id="element-1029" data-mp="37aphaidukq00">
              <strong data-id="element-1030" data-mp="3c7685kcfligs">Institutional Partners:</strong> If you access SyNeural
              through an educational institution, employer, or government
              program, we may share relevant data with that organization as
              agreed upon
            </li>
            <li data-id="element-1031" data-mp="3hecy6wym0lw8">
              <strong data-id="element-1032" data-mp="3cljtua71zfko">Service Providers:</strong> With trusted third-party
              vendors who help us operate our platform (e.g., hosting,
              analytics, customer support)
            </li>
            <li data-id="element-1033" data-mp="33klpfqspd61k">
              <strong data-id="element-1034" data-mp="33leqcb4zd1bs">Research Partners:</strong> Aggregated, anonymized data
              may be shared with research institutions for academic and
              scientific purposes
            </li>
            <li data-id="element-1035" data-mp="2tm5zvs9bq4u6">
              <strong data-id="element-1036" data-mp="2tnqv7k3gprt3">Legal Requirements:</strong> When required by law, court
              order, or to protect our rights and safety
            </li>
            <li data-id="element-1037" data-mp="rvccwjx0alc5">
              <strong data-id="element-1038" data-mp="rv2ov1viuo93">Business Transfers:</strong> In connection with a merger,
              acquisition, or sale of assets
            </li>
          </ul>
        </motion.div>

        {/* Data Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1039" data-mp="2e1da6eqmdxha">
          <div className="flex items-center gap-4 mb-6" data-id="element-1040" data-mp="2j0py72t89alg">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1041" data-mp="2iz6j3fjb55l9">
              <Lock size={24} data-id="element-1042" data-mp="1qcc1cqdvkkbh" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1043" data-mp="1qdmza9kn2xuo">
              Data Security
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1044" data-mp="2531e3x6zi7hg">
            We implement industry-standard security measures to protect your
            information, including:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1045" data-mp="1ciudite5olfn">
            <li data-id="element-1046" data-mp="17lb0txx3wgpt">Encryption of data in transit and at rest</li>
            <li data-id="element-1047" data-mp="3j8o2ueg3ykec">Secure authentication and access controls</li>
            <li data-id="element-1048" data-mp="3j7cvy5rd8wj0">Regular security audits and vulnerability assessments</li>
            <li data-id="element-1049" data-mp="3o1h3or8gh8l5">Employee training on data protection and privacy practices</li>
            <li data-id="element-1050" data-mp="3a9j16z992axy">
              Incident response procedures and breach notification protocols
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1051" data-mp="3eacqnx2cbxsy">
            While we take reasonable precautions, no method of transmission over
            the internet or electronic storage is 100% secure. We cannot
            guarantee absolute security but are committed to protecting your
            information to the best of our ability.
          </p>
        </motion.div>

        {/* Your Rights and Choices */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1052" data-mp="1uivbwh0094b">
          <div className="flex items-center gap-4 mb-6" data-id="element-1053" data-mp="n3lss5t6qo21">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1054" data-mp="n45bhoqntgh4">
              <Shield size={24} data-id="element-1055" data-mp="ibc4svbmry2e" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1056" data-mp="ebto6bque95d">
              Your Rights and Choices
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1057" data-mp="9ds2dj217vq7">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="list-disc list-inside space-y-3 text-ink-muted ml-4" data-id="element-1058" data-mp="3vivrr51s3d5l">
            <li data-id="element-1059" data-mp="3rhtqkioawr0z">
              <strong data-id="element-1060" data-mp="3ribve3p14dki">Access:</strong> Request a copy of the personal
              information we hold about you
            </li>
            <li data-id="element-1061" data-mp="1tqr445rbb20o">
              <strong data-id="element-1062" data-mp="1trir4s6ufrfc">Correction:</strong> Request correction of inaccurate or
              incomplete information
            </li>
            <li data-id="element-1063" data-mp="37o3jscrprxan">
              <strong data-id="element-1064" data-mp="37ololxsfzju6">Deletion:</strong> Request deletion of your personal
              information (subject to legal obligations)
            </li>
            <li data-id="element-1065" data-mp="3cllry0xwq8hl">
              <strong data-id="element-1066" data-mp="3cl2asmg2zo4m">Portability:</strong> Request a copy of your data in a
              structured, machine-readable format
            </li>
            <li data-id="element-1067" data-mp="3lfqggdpkj7ty">
              <strong data-id="element-1068" data-mp="3leffsmrf6g72">Opt-Out:</strong> Opt out of certain data processing
              activities, such as marketing communications
            </li>
            <li data-id="element-1069" data-mp="2orke5xg4criu">
              <strong data-id="element-1070" data-mp="2to1858a0203u">Withdraw Consent:</strong> Withdraw consent for data
              processing where consent is the legal basis
            </li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1071" data-mp="2xs0j7bcig6pt">
            To exercise these rights, please contact us at{' '}
            <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1072" data-mp="czovmvd6jzdb">
              privacy@syneural.com
            </a>
            .
          </p>
        </motion.div>

        {/* Data Retention */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1073" data-mp="gjp3gxkemvw9">
          <div className="flex items-center gap-4 mb-6" data-id="element-1074" data-mp="lj1za7t97daz">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1075" data-mp="zf5qrtcqt7jr">
              <FileText size={24} data-id="element-1076" data-mp="ugkmniude3q1" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1077" data-mp="1960ez39juh7u">
              Data Retention
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1078" data-mp="148i2j2wbpjv3">
            We retain your personal information for as long as necessary to
            provide our services, comply with legal obligations, resolve
            disputes, and enforce our agreements. Assessment data may be
            retained longer for research and platform improvement purposes, but
            will be anonymized where possible. You may request deletion of your
            account and associated data at any time, subject to legal retention
            requirements.
          </p>
        </motion.div>

        {/* Children's Privacy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1079" data-mp="1rszhc8war4ov">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1080" data-mp="146swun5orwhr">
            Children's Privacy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1081" data-mp="v4bc6d5ojbwv">
            SyNeural's services are not directed to children under 13 years of
            age. We do not knowingly collect personal information from children
            under 13. If we become aware that we have collected information from
            a child under 13, we will take steps to delete such information. For
            educational use cases involving minors, we work with schools and
            institutions to ensure appropriate parental consent and data
            protection measures are in place.
          </p>
        </motion.div>

        {/* International Data Transfers */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1082" data-mp="3kq4p6wtkts9c">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1083" data-mp="36rdhbs5b52tn">
            International Data Transfers
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1084" data-mp="1vchu3hslht1h">
            Your information may be transferred to and processed in countries
            other than your country of residence. We ensure that such transfers
            comply with applicable data protection laws and implement
            appropriate safeguards, such as standard contractual clauses, to
            protect your information.
          </p>
        </motion.div>

        {/* Changes to This Policy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1085" data-mp="onykuyoqtow1">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1086" data-mp="13ha6c8ghbxlt">
            Changes to This Privacy Policy
          </h2>
          <p className="text-ink-muted leading-relaxed" data-id="element-1087" data-mp="35azrn68k47st">
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technology, legal requirements, or other
            factors. We will notify you of material changes by posting the
            updated policy on our website and updating the "Last Updated" date.
            Your continued use of our services after such changes constitutes
            acceptance of the updated policy.
          </p>
        </motion.div>

        {/* Contact Us */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-1088" data-mp="2v8nmpere8pys">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1089" data-mp="2bh0j76z7l0fp">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1090" data-mp="3rywkuvooftmj">
            If you have questions, concerns, or requests regarding this Privacy
            Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-1091" data-mp="3ayls58m4na3">
            <p data-id="element-1092" data-mp="3bq6gl0o90ll">
              <strong data-id="element-1093" data-mp="3c9rwbpj674d">Email:</strong>{' '}
              <a href="mailto:privacy@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1094" data-mp="3lgtiyqsywew1">
                privacy@syneural.com
              </a>
            </p>
            <p data-id="element-1095" data-mp="hzl32chsvp3y">
              <strong data-id="element-1096" data-mp="i15u3s4wopx2">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1097" data-mp="myo7pvtvbtbe">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}